import { useMemo, useState, type CSSProperties } from "react";
import type { DemoAttendeeRow } from "../sql";
import { uniqueDemos, collapseToCompany } from "../helpers";
import type { ViewMode } from "../helpers";
import { fmtN, fmtPct, pctVal } from "../../../lib/funnel/helpers";
import { InfoButton } from "../../../lib/funnel/InfoButton";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

// One row per demoer. Outcome stats are measured at the unit selected by the
// page-wide view toggle: attendees in attendee mode, companies in company
// mode (first-touch attributed to the demoer who ran the earliest demo for
// that company). High-level stats only, not a leaderboard; sample sizes per
// demoer are usually too small to read as performance.

type Row = {
  demoer: string;
  demos: number;
  units: number;
  signedUp: number;
  activated: number;
  paid: number;
  avgDaysToSignup: number | null;
};

type SortKey = "demos" | "signupPct" | "paidPct" | "units" | "avgDays";

// Per-column heat shading on a single red->green diverging scale, scaled
// relative to the best and worst demoer in the *current* filter (not an
// absolute scale). The column's worst value shades red, its best shades green,
// and the middle stays unshaded. `higherIsBetter` flips the sense for avg days
// to sign-up, where a lower (faster) value is the good end.
const HEAT_GOOD = [82, 183, 136] as const;  // jade green
const HEAT_BAD = [220, 106, 76] as const;   // coral red

function heatStyle(
  value: number | null,
  min: number,
  max: number,
  higherIsBetter: boolean,
): CSSProperties {
  if (value == null || max <= min) return {};
  const t = (value - min) / (max - min);
  const goodness = higherIsBetter ? t : 1 - t;
  const [r, g, b] = goodness >= 0.5 ? HEAT_GOOD : HEAT_BAD;
  const alpha = Math.abs(goodness - 0.5) * 2 * 0.55;
  return { background: `rgba(${r},${g},${b},${alpha.toFixed(3)})` };
}

type Extent = { min: number; max: number };
const extentOf = (vals: number[]): Extent =>
  vals.length === 0 ? { min: 0, max: 0 } : { min: Math.min(...vals), max: Math.max(...vals) };

export function DemoerBreakdown({
  rows,
  viewMode,
  selectedDemoers,
  onToggleDemoer,
  pagePills,
}: {
  rows: DemoAttendeeRow[];
  viewMode: ViewMode;
  selectedDemoers: string[];
  onToggleDemoer: (demoer: string) => void;
  pagePills?: FilterPillSpec[];
}) {
  const [sortKey, setSortKey] = useState<SortKey>("demos");
  const unitLabel = viewMode === "company" ? "Companies" : "Attendees";
  const selectedSet = useMemo(() => new Set(selectedDemoers), [selectedDemoers]);

  const data = useMemo<Row[]>(() => {
    const byDemoer = new Map<string, {
      demoer: string; demos: number; units: number; signedUp: number; activated: number; paid: number;
      daysSum: number; daysCount: number;
    }>();
    const empty = (demoer: string) => ({
      demoer, demos: 0, units: 0, signedUp: 0, activated: 0, paid: 0, daysSum: 0, daysCount: 0,
    });

    // Demos count: unique demo_external_id per organiser, from the raw
    // attendee-level rows (so a demoer gets credit for every demo they ran,
    // not just the earliest one against a given company in company mode).
    const seenDemo = new Set<string>();
    for (const r of rows) {
      const demoer = r.organiser_email ?? "(unknown)";
      const cur = byDemoer.get(demoer) ?? empty(demoer);
      const demoKey = `${demoer}::${r.demo_external_id}`;
      if (!seenDemo.has(demoKey)) {
        cur.demos += 1;
        seenDemo.add(demoKey);
      }
      byDemoer.set(demoer, cur);
    }

    const outcomeRows = viewMode === "company" ? collapseToCompany(rows) : rows;
    for (const r of outcomeRows) {
      const demoer = r.organiser_email ?? "(unknown)";
      const cur = byDemoer.get(demoer) ?? empty(demoer);
      cur.units += 1;
      if (r.signup_timing_bucket !== "never") cur.signedUp += 1;
      if (r.activated) cur.activated += 1;
      if (r.on_paid_plan === true) cur.paid += 1;
      // Average days-to-signup uses only positive post-demo signups, matching
      // the histogram's denominator. Negative (signed up before demo) and
      // never values are excluded so the average reads as a forward time.
      if (
        r.days_demo_to_signup != null &&
        r.days_demo_to_signup >= 0 &&
        r.signup_timing_bucket !== "never" &&
        r.signup_timing_bucket !== "before"
      ) {
        cur.daysSum += r.days_demo_to_signup;
        cur.daysCount += 1;
      }
      byDemoer.set(demoer, cur);
    }

    const arr: Row[] = Array.from(byDemoer.values()).map((c) => ({
      demoer: c.demoer,
      demos: c.demos,
      units: c.units,
      signedUp: c.signedUp,
      activated: c.activated,
      paid: c.paid,
      avgDaysToSignup: c.daysCount > 0 ? c.daysSum / c.daysCount : null,
    }));
    return arr.sort((a, b) => {
      switch (sortKey) {
        case "demos": return b.demos - a.demos;
        case "units": return b.units - a.units;
        case "signupPct": return (b.signedUp / Math.max(1, b.units)) - (a.signedUp / Math.max(1, a.units));
        case "paidPct": return (b.paid / Math.max(1, b.units)) - (a.paid / Math.max(1, a.units));
        case "avgDays": return (b.avgDaysToSignup ?? -1) - (a.avgDaysToSignup ?? -1);
      }
    });
  }, [rows, sortKey, viewMode]);

  const extents = useMemo(() => ({
    signup: extentOf(data.map((r) => pctVal(r.signedUp, r.units))),
    paid: extentOf(data.map((r) => pctVal(r.paid, r.units))),
    days: extentOf(data.filter((r) => r.avgDaysToSignup != null).map((r) => r.avgDaysToSignup as number)),
  }), [data]);

  const totalDemos = useMemo(() => uniqueDemos(rows).length, [rows]);

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (selectedDemoers.length > 0) {
    pills.push({
      kind: "demoer",
      label: `${selectedDemoers.length} demoer${selectedDemoers.length === 1 ? "" : "s"} highlighted`,
    });
  }

  return (
    <section className="card">
      <SectionCardHeader
        title="Demoer breakdown"
        subtitle={
          <>
            {`${viewMode === "company" ? "Company-level" : "Attendee-level"} stats per demoer, ${data.length} demoers, ${fmtN(totalDemos)} demos. `}
            Shaded columns (sign-up rate, avg days to sign-up, paid rate) use a red to green scale within each column, relative to these demoers: green is the best demoer, red the worst, with faster sign-up counting as better.
          </>
        }
        filterPills={pills}
      />
      <div className="table-wrap">
        <table className="t" style={{ width: "100%", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Demoer</th>
              <SortableTh label="Demos" k="demos" sortKey={sortKey} onSort={setSortKey} />
              <SortableTh label={unitLabel} k="units" sortKey={sortKey} onSort={setSortKey} />
              <th style={{ textAlign: "right" }}>Signed up</th>
              <SortableTh label="Sign-up %" k="signupPct" sortKey={sortKey} onSort={setSortKey} />
              <SortableTh label="Avg days to sign-up" k="avgDays" sortKey={sortKey} onSort={setSortKey} />
              <th style={{ textAlign: "right" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                  Activated
                  <InfoButton label="Activated definition" align="right">
                    <p style={{ margin: 0 }}>
                      Activated = signed-up attendee has at least one non-demo, non-deleted <code>meeting_synopsis</code> row.
                      Same definition used by the Sign-up Activation dashboard.
                    </p>
                  </InfoButton>
                </span>
              </th>
              <th style={{ textAlign: "right" }}>Paid</th>
              <SortableTh label="Paid %" k="paidPct" sortKey={sortKey} onSort={setSortKey} />
            </tr>
          </thead>
          <tbody>
            {data.map((r) => {
              const active = selectedSet.has(r.demoer);
              return (
              <tr
                key={r.demoer}
                className={active ? "is-highlighted" : undefined}
                onClick={() => onToggleDemoer(r.demoer)}
                title={active ? "Clear demoer filter" : "Filter dashboard by this demoer"}
                style={{ cursor: "pointer" }}
              >
                <td>{r.demoer}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.demos)}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.units)}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.signedUp)}</td>
                <td style={{ textAlign: "right", ...heatStyle(pctVal(r.signedUp, r.units), extents.signup.min, extents.signup.max, true) }}>{fmtPct(r.signedUp, r.units)}</td>
                <td style={{ textAlign: "right", ...heatStyle(r.avgDaysToSignup, extents.days.min, extents.days.max, false) }}>
                  {r.avgDaysToSignup == null ? <span className="muted">—</span> : `${Math.round(r.avgDaysToSignup)}d`}
                </td>
                <td style={{ textAlign: "right" }}>{fmtN(r.activated)}</td>
                <td style={{ textAlign: "right" }}>{fmtN(r.paid)}</td>
                <td style={{ textAlign: "right", ...heatStyle(pctVal(r.paid, r.units), extents.paid.min, extents.paid.max, true) }}>{fmtPct(r.paid, r.units)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortableTh({
  label, k, sortKey, onSort,
}: {
  label: string; k: SortKey; sortKey: SortKey; onSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      style={{ textAlign: "right", cursor: "pointer", color: active ? "var(--color-teal)" : undefined }}
    >
      {label}{active ? " ▼" : ""}
    </th>
  );
}
